import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from 'reactstrap';
import { ArrowLeft, ArrowRight } from 'react-feather';
import { connect } from 'react-redux';
import {
  createBrandRequest as createBrandRequestAction,
  updateBrandRequest as updateBrandRequestAction
} from '../../ReduxToolkit/Reducers/brandRequests';
import Step1BrandInfo from '../../Components/BrandRequests/Step1BrandInfo';
import Step2BrandInSpaceSetup from '../../Components/BrandRequests/Step2BrandInSpaceSetup';
import Step3Exclusions from '../../Components/BrandRequests/Step3Exclusions';
import Step4PreferredRetailer from '../../Components/BrandRequests/Step4PreferredRetailer';
import Step5FindMatchesSetup from '../../Components/BrandRequests/Step5/Step5FindMatchesSetup';
import Step5UniqueMethodOptions from '../../Components/BrandRequests/Step5/Step5UniqueMethodOptions';
import Step5PutBrandRetailers from '../../Components/BrandRequests/Step5/Step5PutBrandRetailers';
import Step5TakeWorkingRetailer from '../../Components/BrandRequests/Step5/Step5TakeWorkingRetailer';
import Step6ReviewSubmit from '../../Components/BrandRequests/Step6ReviewSubmit';
import SuccessModal from '../../Components/BrandRequests/SuccessModal';
import { useSelector } from 'react-redux';

const STEPS = [
  'Brand Info',
  'Brand In Your Space Setup',
  'Exclusions',
  'Preferred Retailer',
  'Find Matches Setup',
  'Review & Submit',
];

const step1Schema = yup.object().shape({
  brand_info: yup.object().shape({
    brand_id: yup
      .mixed()
      .optional()
      .test('is-positive-int', 'Brand ID must be a valid positive number', (val) => {
        if (val === '' || val == null) return true;
        const n = Number(val);
        return Number.isInteger(n) && n > 0;
      }),
    brand_name: yup.string().required('Brand name is required'),
    website: yup
      .string()
      .matches(/^(https?:\/\/)?.*\..+/, 'Must be a valid URL with domain extension').required('Website is required'),
    deadline: yup.string().optional(),
    markets_of_interest: yup.array().optional(),
    remove_existing_next_to_brands_competitors: yup.boolean(),
  }),
});

const step2Schema = yup.object().shape({
  brand_in_space: yup.object().shape({
    brands: yup.array().of(
      yup.object().shape({
        brand_id: yup
          .mixed()
          .optional()
          .test('is-positive-int', 'Brand ID must be a valid positive number', (val) => {
            if (val === '' || val == null) return true;
            const n = Number(val);
            return Number.isInteger(n) && n > 0;
          }),
        name: yup.string().required('Brand name is required'),
        website: yup
          .string()
          .matches(/^(https?:\/\/)?.*\..+/, 'Must be a valid URL with domain extension').required('Website is required'),
      })
    ).min(1, 'Add at least one brand'),
  }),
});

const step3Schema = yup.object().shape({
  exclusions: yup.object().shape({
    brands: yup.array().of(
      yup.object().shape({
        brand_id: yup
          .mixed()
          .optional()
          .test('is-positive-int', 'Brand ID must be a valid positive number', (val) => {
            if (val === '' || val == null) return true;
            const n = Number(val);
            return Number.isInteger(n) && n > 0;
          }),
        name: yup.string().optional('Name is required'),
        website: yup
          .string()
          .test({
            test: (value) => {
              if (!value) return true;
              return /^(https?:\/\/)?.*\..+/.test(value);
            },
            message: 'Must be a valid URL with domain extension',
          })
          .optional('Website is required'),
      })
    ).optional(),
  }),
});

const step4Schema = yup.object().shape({
  preferred_retailer: yup.string().optional().nullable(),
});

const step5PutBrandSchema = yup.object().shape({
  find_matches: yup.object().shape({
    brand_a: yup.object().shape({
      brand_id: yup
        .mixed()
        .optional()
        .test('is-positive-int', 'Brand ID must be a valid positive number', (val) => {
          if (val === '' || val == null) return true;
          const n = Number(val);
          return Number.isInteger(n) && n > 0;
        }),
      name: yup.string().required('Brand A name is required'),
      website: yup
        .string()
        .matches(/^(https?:\/\/)?.*\..+/, 'Must be a valid URL with domain extension')
        .required('Website is required'),
    }),
    brand_b: yup.array().of(
      yup.object().shape({
        brand_id: yup
          .mixed()
          .optional()
          .test('is-positive-int', 'Brand ID must be a valid positive number', (val) => {
            if (val === '' || val == null) return true;
            const n = Number(val);
            return Number.isInteger(n) && n > 0;
          }),
        name: yup.string().required('Brand B name is required'),
        website: yup
          .string()
          .matches(/^(https?:\/\/)?.*\..+/, 'Must be a valid URL with domain extension')
          .required('Website is required'),
      })
    ).min(1, 'Add at least one Brand B'),
    match_type: yup.string().required('Match type is required'),
    remove_previous_matching: yup.boolean(),
  }),
});

const step5TakeWorkingSchema = yup.object().shape({
  find_matches: yup.object().shape({
    match_type: yup.string().required('Match type is required'),
    remove_previous_matching: yup.boolean(),
  }),
});

function normalizeWebsiteForExclusionMatch(url) {
  if (!url || typeof url !== 'string') return '';
  let s = url.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.replace(/^www\./, '');
  const host = s.split('/')[0].split('?')[0].split('#')[0];
  return host.replace(/\.+$/, '');
}

/** Clears prior exclusion errors on Brand B fields, then sets errors for matches. Returns true if any violation. */
function applyBrandBExclusionErrors(getValues, setError, clearErrors) {
  const exclusionBrands = getValues('exclusions.brands') || [];
  const brandB = getValues('find_matches.brand_b') || [];

  brandB.forEach((_, index) => {
    clearErrors(`find_matches.brand_b.${index}.name`);
    clearErrors(`find_matches.brand_b.${index}.website`);
  });

  const exclusionNames = new Set(
    exclusionBrands
      .map((b) => (b?.name || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const exclusionWebsites = new Set(
    exclusionBrands
      .map((b) => normalizeWebsiteForExclusionMatch(b?.website))
      .filter(Boolean)
  );

  let hasViolation = false;
  brandB.forEach((row, index) => {
    const nameNorm = (row?.name || '').trim().toLowerCase();
    const webNorm = normalizeWebsiteForExclusionMatch(row?.website);
    if (nameNorm && exclusionNames.has(nameNorm)) {
      setError(`find_matches.brand_b.${index}.name`, {
        type: 'exclusion',
        message: 'This brand name is in your exclusions list',
      });
      hasViolation = true;
    }
    if (webNorm && exclusionWebsites.has(webNorm)) {
      setError(`find_matches.brand_b.${index}.website`, {
        type: 'exclusion',
        message: 'This website is in your exclusions list',
      });
      hasViolation = true;
    }
  });
  return hasViolation;
}

const defaultValues = {
  brand_info: {
    brand_id: '',
    brand_name: '',
    website: '',
    deadline: '',
    markets_of_interest: [],
    remove_existing_next_to_brands_competitors: false,
  },
  brand_in_space: {
    brands: [
      { brand_id: '', name: '', website: '' },
      { brand_id: '', name: '', website: '' },
      { brand_id: '', name: '', website: '' },
      { brand_id: '', name: '', website: '' },
      { brand_id: '', name: '', website: '' },
    ],
  },
  exclusions: {
    brands: [{ brand_id: '', name: '', website: '' }],
  },
  preferred_retailer: '',
  find_matches: {
    type: '',
    unique_option: '',
    brand_a: { brand_id: '', name: '', website: '' },
    brand_b: [{ brand_id: '', name: '', website: '' }],
    brands_in_space: [],
    match_type: 'match',
    remove_previous_matching: false,
  },
};

const CreateBrandRequest = ({
  createBrandRequest,
  updateBrandRequest,
  isLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth?.user || {});
  const [currentStep, setCurrentStep] = useState(1);
  const [showUniqueOptions, setShowUniqueOptions] = useState(false);
  const [selectedUniqueOption, setSelectedUniqueOption] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const getCurrentSchema = () => {
    if (currentStep === 1) return step1Schema;
    if (currentStep === 2) return step2Schema;
    if (currentStep === 3) return step3Schema;
    if (currentStep === 4) return step4Schema;
    if (currentStep === 5 && selectedUniqueOption === 'put_brand_retailers') return step5PutBrandSchema;
    if (currentStep === 5 && selectedUniqueOption === 'take_working_retailers') return step5TakeWorkingSchema;
    return yup.object().shape({});
  };
  const methods = useForm({
    resolver: yupResolver(getCurrentSchema()),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = methods;


  useEffect(() => {
    // Check if we're in edit mode from location state
    if (location.state?.brandRequest) {
      const { brandRequest, editStep } = location.state;
      setIsEditMode(true);
      setEditingId(brandRequest.id);
      console.log('brandRequest', brandRequest);
      // Pre-populate form with existing data
      const formData = brandRequest;

      // Set all form values
      if (formData.brand_info) {
        setValue('brand_info.brand_id', formData.brand_info.brand_id || '');
        setValue('brand_info.brand_name', formData.brand_info.brand_name || '');
        setValue('brand_info.website', formData.brand_info.website || '');
        setValue('brand_info.deadline', formData.brand_info.deadline || '');
        setValue('brand_info.markets_of_interest', formData.brand_info.markets_of_interest || []);
        setValue(
          'brand_info.remove_existing_next_to_brands_competitors',
          formData.brand_info.remove_existing_next_to_brands_competitors || false
        );
      }

      if (formData.brand_in_space?.brands) {
        setValue('brand_in_space.brands', formData.brand_in_space.brands);
      }

      if (formData.exclusions?.brands) {
        setValue('exclusions.brands', formData.exclusions.brands);
      }

      if (formData.preferred_retailer) {
        setValue('preferred_retailer', formData.preferred_retailer);
      }

      if (formData.find_matches) {
        const findMatches = { ...formData.find_matches };
        if (Array.isArray(findMatches.brand_a)) {
          findMatches.brand_a =
            findMatches.brand_a[0] || { brand_id: '', name: '', website: '' };
        }
        setValue('find_matches', findMatches);

        // Set step 5 states based on existing data
        if (formData.find_matches.type === 'unique') {
          if (formData.find_matches.unique_option === 'put_brand_retailers') {
            setShowUniqueOptions(false);
            setSelectedUniqueOption('put_brand_retailers');
          } else if (formData.find_matches.unique_option === 'take_working_retailers') {
            setShowUniqueOptions(false);
            setSelectedUniqueOption('take_working_retailers');
          }
        }
      }

      // Set the step based on editStep parameter
      if (editStep) {
        const stepMap = {
          'brand_info': 1,
          'brand_in_space': 2,
          'exclusions': 3,
          'preferred_retailer': 4,
          'find_matches': 5,
          'review': 6,
        };
        setCurrentStep(stepMap[editStep] || 1);
      }
    }
  }, [location.state, setValue]);


  const onNext = handleSubmit((data) => {
    console.log(`Step ${currentStep} data:`, data);
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  });

  const onBack = () => {
    if (currentStep === 5 && selectedUniqueOption) {
      setSelectedUniqueOption(null);
      setShowUniqueOptions(true);
    } else if (currentStep === 5 && showUniqueOptions) {
      setShowUniqueOptions(false);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowUniqueOptions(false);
      setSelectedUniqueOption(null);
    } else {
      navigate('/brand-requests');
    }
  };

  const handleSelectMethod = (methodType) => {
    if (methodType === 'unique') {
      setShowUniqueOptions(true);
    } else {
      setValue('find_matches.type', methodType);
      console.log(`Selected method: ${methodType}`);
      setCurrentStep(6);
    }
  };

  const handleSelectUniqueOption = (optionType) => {
    setValue('find_matches.type', 'unique');
    setValue('find_matches.unique_option', optionType);

    // // If "take_working_retailers", copy brands from step 2 to step 5
    // if (optionType === 'take_working_retailers') {
    //   const step2Brands = getValues('brand_in_space.brands') || [];
    //   setValue('find_matches.brands_in_space', step2Brands);
    // }

    setSelectedUniqueOption(optionType);
    setShowUniqueOptions(false);
    console.log(`Selected unique option: ${optionType}`);
  };

  const handleBackToUniqueOptions = () => {
    setSelectedUniqueOption(null);
    setShowUniqueOptions(true);
  };

  const handleContinueFromUniqueForm = handleSubmit((data) => {
    console.log('Unique form data:', data);
    if (selectedUniqueOption === 'put_brand_retailers') {
      const hasExclusionViolation = applyBrandBExclusionErrors(
        getValues,
        setError,
        clearErrors
      );
      if (hasExclusionViolation) return;
    }
    setCurrentStep(6);
  });

  const handleEditStep = (stepNumber) => {
    setCurrentStep(stepNumber);
    setShowUniqueOptions(false);
    setSelectedUniqueOption(null);
  };

  const handleSubmitRequest = async () => {
    const finalData = control._formValues;
    console.log(isEditMode ? 'Update request data:' : 'Create request data:', finalData);

    if (finalData?.exclusions?.brands?.length === 1) {
      if (finalData?.exclusions?.brands[0]?.name === '' && finalData?.exclusions?.brands[0]?.website === '') {
        finalData.exclusions.brands = [];
      }
    }

    if (finalData?.brand_info?.markets_of_interest?.length) {
      finalData.brand_info.markets_of_interest = finalData.brand_info.markets_of_interest.map((market) => ({ label: market }));
    }

    console.log('finalData', finalData);

    let result;
    if (isEditMode) {
      console.log('Updating brand request with ID:', editingId);
      result = await updateBrandRequest(editingId, finalData, false, user?.name);
    } else {
      console.log('Creating new brand request');
      result = await createBrandRequest(finalData);
    }

    if (result?.statusCode === 200) {
      setSuccessModal(true);
    }
  };

  const handleSkipMatches = () => {
    setValue('find_matches.type', 'standard');
    setCurrentStep(6);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BrandInfo control={control} errors={errors} />;
      case 2:
        return <Step2BrandInSpaceSetup control={control} errors={errors} />;
      case 3:
        return <Step3Exclusions control={control} errors={errors} />;
      case 4:
        return <Step4PreferredRetailer control={control} errors={errors} />;
      case 5:
        if (selectedUniqueOption === 'put_brand_retailers') {
          return (
            <Step5PutBrandRetailers
              control={control}
              errors={errors}
              onBackToSelection={handleBackToUniqueOptions}
            />
          );
        }
        if (selectedUniqueOption === 'take_working_retailers') {
          return (
            <Step5TakeWorkingRetailer
              control={control}
              errors={errors}
              onBackToSelection={handleBackToUniqueOptions}
            />
          );
        }
        if (showUniqueOptions) {
          return (
            <Step5UniqueMethodOptions
              onSelectOption={handleSelectUniqueOption}
              onBackToSelection={() => setShowUniqueOptions(false)}
            />
          );
        }
        return (
          <Step5FindMatchesSetup
            onSelectMethod={handleSelectMethod}
          />
        );
      case 6:
        return <Step6ReviewSubmit onEdit={handleEditStep} />;
      default:
        return <div>Step {currentStep} - Coming soon</div>;
    }
  };

  return (
    <div className="page-body" style={{ backgroundColor: '#f5f5f0', minHeight: '100vh' }}>
      <Container fluid style={{ padding: '28px 32px' }}>
        <FormProvider {...methods}>
          {/* Page Header */}
          <div style={{ marginBottom: '24px' }}>
            <button
              type="button"
              onClick={() => navigate('/brand-requests')}
              style={backLinkStyle}
            >
              <ArrowLeft size={16} />
              <span>{isEditMode ? 'Edit Brand Request' : 'Add Brand Requests'}</span>
            </button>
            <p style={{ color: '#888', fontSize: '13px', marginTop: '4px', marginBottom: 0 }}>
              {isEditMode
                ? 'Update the brand request details below'
                : 'Follow these simple steps below to add the brand request'}
            </p>
          </div>

          {/* Stepper */}
          <div style={stepperWrapperStyle}>
            {STEPS.map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = stepNum === currentStep;
              const isCompleted = stepNum < currentStep;
              return (
                <React.Fragment key={label}>
                  <div style={stepItemStyle}>
                    <div
                      style={{
                        ...stepCircleStyle,
                        background: isActive || isCompleted ? '#222' : '#fff',
                        color: isActive || isCompleted ? '#fff' : '#aaa',
                        border: isActive || isCompleted ? '2px solid #222' : '2px solid #d0d0d0',
                      }}
                    >
                      {stepNum}
                    </div>
                    <span
                      style={{
                        ...stepLabelStyle,
                        color: '#212121',
                        fontWeight: isActive ? '600' : '400',
                      }}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div style={stepDividerStyle} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Form Card */}
          <div style={cardWrapperStyle}>
            {renderStep()}
          </div>

          {/* Navigation Buttons */}
          {currentStep !== 5 && currentStep !== 6 && (
            <div style={navRowStyle}>
              <button
                type="button"
                onClick={onBack}
                style={backBtnStyle}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={onNext}
                style={nextBtnStyle}
              >
                Next
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Navigation for Step 5 - Main selection (Standard/Unique) */}
          {currentStep === 5 && !showUniqueOptions && !selectedUniqueOption && (
            <div style={navRowStyle}>
              <button
                type="button"
                onClick={onBack}
                style={backBtnStyle}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={handleSkipMatches}
                style={skipAndContinueBtnStyle}
              >
                Skip & Continue
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Navigation for Step 5 - Unique options selection */}
          {currentStep === 5 && showUniqueOptions && (
            <div style={navRowStyle}>
              <div></div>
              <button
                type="button"
                onClick={handleSkipMatches}
                style={skipAndContinueBtnStyle}
              >
                Skip & Continue
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Navigation for Step 5 - Put Brand Retailers form */}
          {currentStep === 5 && selectedUniqueOption === 'put_brand_retailers' && (
            <div style={navRowStyle}>
              <button
                type="button"
                onClick={onBack}
                style={backBtnStyle}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={handleContinueFromUniqueForm}
                style={nextBtnStyle}
              >
                Continue
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Navigation for Step 5 - Take Working Retailer form */}
          {currentStep === 5 && selectedUniqueOption === 'take_working_retailers' && (
            <div style={navRowStyle}>
              <button
                type="button"
                onClick={onBack}
                style={backBtnStyle}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={handleContinueFromUniqueForm}
                style={nextBtnStyle}
              >
                Continue
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* Navigation for Step 6 - Review & Submit */}
          {currentStep === 6 && (
            <div style={navRowStyle}>
              <button
                type="button"
                onClick={onBack}
                style={backBtnStyle}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmitRequest}
                style={nextBtnStyle}
                disabled={isLoading}
              >
                {isLoading ? 'Submitting...' : (isEditMode ? 'Update Request' : 'Submit Request')}
              </button>
            </div>
          )}

          <SuccessModal open={successModal} isEditMode={isEditMode} onClose={() => {
            setSuccessModal(false)
            navigate(-1)
          }} />
        </FormProvider>
      </Container>
    </div>
  );
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: '18px',
  fontWeight: '600',
  color: '#222',
  cursor: 'pointer',
};

const stepperWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  background: '#fff',
  borderRadius: '8px',
  padding: '16px 24px',
  marginBottom: '32px',
  overflowX: 'auto',
};

const stepItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const stepCircleStyle = {
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: '600',
  flexShrink: 0,
};

const stepLabelStyle = {
  fontSize: '13px',
};

const stepDividerStyle = {
  flex: 1,
  minWidth: '32px',
  borderTop: '1.5px dashed #d0d0d0',
  margin: '0 12px',
};

const cardWrapperStyle = {
  display: 'flex',
  justifyContent: 'center',
};

const navRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: '500px',
  margin: '24px auto 0',
};

const backBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  fontSize: '13px',
  color: '#212121',
  cursor: 'pointer',
  padding: '8px 4px',
};

const nextBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#212121',
  border: 'none',
  color: '#fff',
  fontSize: '13px',
  fontWeight: '500',
  padding: '10px 24px',
  borderRadius: '6px',
  cursor: 'pointer',
};

const skipAndContinueBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#212121',
  border: 'none',
  color: '#fff',
  fontSize: '13px',
  fontWeight: '500',
  padding: '10px 24px',
  borderRadius: '6px',
  cursor: 'pointer',
};

const mapStateToProps = (state) => ({
  isLoading: state.brandRequests.isLoading,
});

const mapDispatchToProps = {
  createBrandRequest: createBrandRequestAction,
  updateBrandRequest: updateBrandRequestAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateBrandRequest);